import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Alert,
  Grid,
  Typography,
  TextField,
} from "@mui/material";
import InvoiceProduct from "../../Empresa/NuevaFactura/InvoiceProduct";
import PurchaseAccounts from "./PurchaseAccounts";

const normalizeBodegas = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.bodegas)) return data.bodegas;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.data)) return data.data;
  return [];
};

const toNum = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(String(v).replace(/,/g, "."));
  return Number.isFinite(n) ? n : 0;
};

const round2 = (v) => Number(Number(v || 0).toFixed(2));

const formatMoney = (v) => Number(v || 0).toFixed(2);

const sameTotals = (a = {}, b = {}) =>
  Number(a.subtotal15 || 0) === Number(b.subtotal15 || 0) &&
  Number(a.subtotal5 || 0) === Number(b.subtotal5 || 0) &&
  Number(a.subtotal0 || 0) === Number(b.subtotal0 || 0) &&
  Number(a.subtotalNoObj || 0) === Number(b.subtotalNoObj || 0) &&
  Number(a.descuento_total || 0) === Number(b.descuento_total || 0) &&
  Number(a.iva15 || 0) === Number(b.iva15 || 0) &&
  Number(a.iva5 || 0) === Number(b.iva5 || 0) &&
  Number(a.total || 0) === Number(b.total || 0);

const PurchaseDetail = ({
  purchaseData,
  onPurchaseDataChange,
  setPayments,
  productRows,
  setProductRows,
  productDescripcion,
  setProductDescripcion,
  accountRows,
  setAccountRows,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [sinMovimiento, setSinMovimiento] = useState(false);
  const [bodegaError, setBodegaError] = useState("");

  useEffect(() => {
    setPayments([]);
  }, [setPayments]);

  useEffect(() => {
    if (purchaseData?.sin_movimiento_inventario !== undefined) {
      setSinMovimiento(purchaseData.sin_movimiento_inventario);
    }
  }, [purchaseData?.sin_movimiento_inventario]);

  useEffect(() => {
    let alive = true;

    const cargarBodegas = async () => {
      setBodegaError("");

      try {
        const res = await fetch("/productos/bodegas/", {
          credentials: "include",
        });

        const data = await res.json().catch(() => ({}));

        if (!alive) return;

        if (!res.ok) {
          setBodegaError("No se pudieron cargar las bodegas. Se usará la bodega principal configurada en backend.");
          return;
        }

        const lista = normalizeBodegas(data).filter(
          (b) => b.activa !== false && b.activo !== false
        );

        if (!lista.length) {
          setBodegaError("No hay bodegas activas configuradas. Se usará la bodega principal configurada en backend.");
          return;
        }

        const principal =
          lista.find((b) => b.es_principal === true || b.principal === true) ||
          lista[0];

        onPurchaseDataChange((prev) => {
          const current = prev?.bodega_id || prev?.bodega || "";
          const selected = current || principal.id || "";

          if (prev?.bodega_id === selected && prev?.bodega === selected) {
            return prev;
          }

          return {
            ...(prev || {}),
            bodega_id: selected,
            bodega: selected,
            bodega_nombre: principal.nombre || "",
            bodega_codigo: principal.codigo || "",
          };
        });
      } catch {
        if (alive) {
          setBodegaError("No se pudieron cargar las bodegas. Se usará la bodega principal configurada en backend.");
        }
      }
    };

    cargarBodegas();

    return () => {
      alive = false;
    };
  }, [onPurchaseDataChange]);

  const handleSinMovimiento = (e) => {
    const checked = e.target.checked;
    setSinMovimiento(checked);

    onPurchaseDataChange((prev) => {
      if (prev?.sin_movimiento_inventario === checked) return prev;

      return {
        ...(prev || {}),
        sin_movimiento_inventario: checked,
      };
    });
  };

  const handleProductDataChange = useCallback(
    (data) => {
      onPurchaseDataChange((prev) => {
        const nextTotals = {
          subtotal15: data?.subtotal15 || 0,
          subtotal5: data?.subtotal5 || 0,
          subtotal0: data?.subtotal0 || 0,
          subtotalNoObj: data?.subtotalNoObj || 0,
          descuento_total: data?.descuento_total || 0,
          iva15: data?.iva15 || 0,
          iva5: data?.iva5 || 0,
          total: data?.total || 0,
        };

        const prevTotals = prev?.productos_totals || {};
        const nextBodegaId = data?.bodega_id || prev?.bodega_id || "";
        const nextBodega = data?.bodega || prev?.bodega || "";

        if (
          sameTotals(prevTotals, nextTotals) &&
          prev?.productos === data?.productos &&
          prev?.bodega_id === nextBodegaId &&
          prev?.bodega === nextBodega
        ) {
          return prev;
        }

        return {
          ...(prev || {}),
          productos: data?.productos || [],
          productos_raw: data?.productos_raw || [],
          productos_totals: nextTotals,
          bodega_id: nextBodegaId,
          bodega: nextBodega,
        };
      });
    },
    [onPurchaseDataChange]
  );

  const handleAccountsDataChange = useCallback(
    (data) => {
      onPurchaseDataChange((prev) => {
        const nextTotals = {
          subtotal15: data?.cuentas_subtotal15 || 0,
          subtotal5: data?.cuentas_subtotal5 || 0,
          subtotal0: data?.cuentas_subtotal0 || 0,
          subtotalNoObj: data?.cuentas_subtotalNoObj || 0,
          descuento_total: data?.cuentas_descuento_total || 0,
          iva15: data?.cuentas_iva15 || 0,
          iva5: data?.cuentas_iva5 || 0,
          total: data?.cuentas_total || 0,
        };

        const prevTotals = prev?.cuentas_totals || {};

        if (
          sameTotals(prevTotals, nextTotals) &&
          prev?.cuentas === data?.cuentas &&
          prev?.gastos === data?.gastos
        ) {
          return prev;
        }

        return {
          ...(prev || {}),
          cuentas: data?.cuentas || [],
          gastos: data?.gastos || [],
          cuentas_raw: data?.cuentas_raw || [],
          cuentas_totals: nextTotals,
        };
      });
    },
    [onPurchaseDataChange]
  );

  const globalTotals = useMemo(() => {
    const productos = purchaseData?.productos_totals || {};
    const cuentas = purchaseData?.cuentas_totals || {};

    const subtotal15 = round2(toNum(productos.subtotal15) + toNum(cuentas.subtotal15));
    const subtotal5 = round2(toNum(productos.subtotal5) + toNum(cuentas.subtotal5));
    const subtotal0 = round2(toNum(productos.subtotal0) + toNum(cuentas.subtotal0));
    const subtotalNoObj = round2(toNum(productos.subtotalNoObj) + toNum(cuentas.subtotalNoObj));
    const descuento = round2(toNum(productos.descuento_total) + toNum(cuentas.descuento_total));
    const iva15 = round2(toNum(productos.iva15) + toNum(cuentas.iva15));
    const iva5 = round2(toNum(productos.iva5) + toNum(cuentas.iva5));
    const total = round2(subtotal15 + subtotal5 + subtotal0 + subtotalNoObj + iva15 + iva5);

    return {
      subtotal15,
      subtotal5,
      subtotal0,
      subtotalNoObj,
      descuento,
      iva15,
      iva5,
      ice: 0,
      total,
    };
  }, [purchaseData?.productos_totals, purchaseData?.cuentas_totals]);

  useEffect(() => {
    onPurchaseDataChange((prev) => {
      if (
        Number(prev?.total_global || 0) === Number(globalTotals.total || 0) &&
        String(prev?.descripcion_global || "") === String(productDescripcion || "")
      ) {
        return prev;
      }

      return {
        ...(prev || {}),
        total_global: globalTotals.total,
        descripcion_global: productDescripcion || "",
      };
    });
  }, [globalTotals.total, productDescripcion, onPurchaseDataChange]);

  return (
    <Box sx={{ mx: "auto", mt: 3 }}>
      <Paper
        sx={{
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 3,
            borderBottom: "1px solid #e0e0e0",
            flexWrap: "wrap",
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={sinMovimiento}
                onChange={handleSinMovimiento}
              />
            }
            label="Sin movimiento de inventario"
          />
        </Box>

        {bodegaError && (
          <Alert severity="warning" sx={{ borderRadius: 0 }}>
            {bodegaError}
          </Alert>
        )}

        <Box
          sx={{
            backgroundColor: "#1565C0",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              backgroundColor: "#1565C0",
              "& .MuiTab-root": {
                color: "#fff",
                textTransform: "none",
                fontWeight: 600,
                minHeight: "48px",
                borderTopLeftRadius: "8px",
                borderTopRightRadius: "8px",
                transition: "all 0.2s ease",
              },
              "& .Mui-selected": {
                backgroundColor: "#fff",
                color: "#1565C0",
                borderTop: "4px solid #1565C0",
                borderLeft: "4px solid #1565C0",
                borderRight: "4px solid #1565C0",
                borderBottom: "none",
                marginTop: "5px",
                marginLeft: "10px",
              },
              "& .MuiTabs-indicator": {
                display: "none",
              },
            }}
          >
            <Tab label="Productos" />
            <Tab label="Cuentas" />
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>
          {tabValue === 0 && (
            <InvoiceProduct
              rows={productRows}
              setRows={setProductRows}
              descripcion={productDescripcion}
              setDescripcion={setProductDescripcion}
              onProductDataChange={handleProductDataChange}
            />
          )}

          {tabValue === 1 && (
            <PurchaseAccounts
              rows={accountRows}
              setRows={setAccountRows}
              onAccountsDataChange={handleAccountsDataChange}
            />
          )}

          <Grid container spacing={3} sx={{ mt: 3 }}>
            <Grid item xs={12} md={8}>
              <Typography sx={{ mb: 1 }}>
                Descripción: <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                value={productDescripcion || ""}
                onChange={(e) => setProductDescripcion(e.target.value)}
                multiline
                minRows={4}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, backgroundColor: "#f8f9fa", borderRadius: 2 }}>
                <Grid container spacing={1}>
                  {[
                    ["Subtotal 15%", globalTotals.subtotal15],
                    ["Subtotal 5%", globalTotals.subtotal5],
                    ["Subtotal 0%", globalTotals.subtotal0],
                    ["Subtotal No Objeto", globalTotals.subtotalNoObj],
                    ["Descuento", globalTotals.descuento],
                    ["IVA 15%", globalTotals.iva15],
                    ["IVA 5%", globalTotals.iva5],
                    ["ICE", globalTotals.ice],
                    ["Total", globalTotals.total],
                  ].map(([label, value], index) => (
                    <React.Fragment key={index}>
                      <Grid item xs={7}>
                        <Typography
                          sx={{
                            color: "#333",
                            fontWeight: label === "Total" ? "bold" : "normal",
                          }}
                        >
                          {label}:
                        </Typography>
                      </Grid>

                      <Grid item xs={5}>
                        <TextField
                          value={`$${formatMoney(value)}`}
                          fullWidth
                          size="small"
                          disabled
                          sx={{
                            "& .Mui-disabled": {
                              backgroundColor: "#f0f0f0 !important",
                              color: "#333 !important",
                              WebkitTextFillColor: "#333 !important",
                            },
                          }}
                        />
                      </Grid>
                    </React.Fragment>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default PurchaseDetail;